import axios from 'axios';

// Base URLs from environment
const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");
const PYTHON_API_URL = (process.env.REACT_APP_PYTHON_API_URL || "").replace("http://", "https://");

// Create axios instance for Node.js backend
export const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Create axios instance for Python backend
export const pythonApi = axios.create({
    baseURL: PYTHON_API_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Request interceptor - attach token to all requests
const requestInterceptor = (config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
};

// Response interceptor - handle errors globally
const responseInterceptor = (response) => response;

const errorInterceptor = async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
        console.error('Network error:', error.message);
    }

    return Promise.reject(error);
};

// Apply interceptors to both instances
api.interceptors.request.use(requestInterceptor);
api.interceptors.response.use(responseInterceptor, errorInterceptor);

pythonApi.interceptors.request.use(requestInterceptor);
pythonApi.interceptors.response.use(responseInterceptor, errorInterceptor);

// API endpoints
export const endpoints = {
    // Auth
    auth: {
        me: () => api.get('/auth/me'),
        signup: (data) => api.post('/auth/signup', data),
        signin: (data) => api.post('/auth/signin', data),
    },

    // Onboarding
    onboarding: {
        getStatus: () => api.get('/api/onboarding/status'),
        savePreferences: (data) => api.post('/api/onboarding/preferences', data),
        complete: () => api.post('/api/onboarding/complete'),
    },

    // Dashboard
    dashboard: {
        getSummary: () => api.get('/api/dashboard/summary'),
        getFeed: (params) => api.get('/api/dashboard/feed', { params }),
    },

    // Relationships - from Python backend (Neo4j graph)
    relationships: {
        getAll: (params) => pythonApi.get('/api/v1/insights/contacts', { params }),
        getById: (id) => pythonApi.get(`/api/v1/insights/sentiment/${id}`),
        toggleVip: (id) => pythonApi.post(`/api/v1/insights/contacts/${id}/vip`),
        getHealth: () => pythonApi.get('/api/v1/insights/relationships'),
        getVips: () => pythonApi.get('/api/v1/insights/vips'),
    },

    // Insights - from Python backend
    insights: {
        getWeekly: () => pythonApi.get('/api/v1/insights/report'),
        getPatterns: () => pythonApi.get('/api/v1/insights/relationships'),
        getAtRisk: () => pythonApi.get('/api/v1/insights/at-risk'),
        getSavedReports: () => pythonApi.get('/api/v1/insights/reports'),
    },

    // OAuth status
    oauth: {
        getStatus: () => api.get('/oauth/status'),
    },

    // Ghost Hunter
    ghosts: {
        getAll: () => pythonApi.get('/api/v1/ghosts/'),
        sendFollowUp: (trackerId, suggestionId) => pythonApi.post(`/api/v1/ghosts/${trackerId}/send`, { suggestion_id: suggestionId }),
        skip: (trackerId) => pythonApi.post(`/api/v1/ghosts/${trackerId}/skip`),
    },

    // Smart Email
    drafts: {
        generate: (data) => pythonApi.post('/api/v1/drafts/generate', data),
        getPending: () => pythonApi.get('/api/v1/drafts/pending'),
        approve: (draftId, editedBody) => pythonApi.post(`/api/v1/drafts/${draftId}/approve`, { edited_body: editedBody }),
        getSettings: () => pythonApi.get('/api/v1/drafts/settings'),
        updateSettings: (data) => pythonApi.put('/api/v1/drafts/settings', data),
    },

    // Tasks (Temporal)
    tasks: {
        start: (data) => pythonApi.post('/api/v1/tasks/start', data),
        signal: (workflowId, signal, status) => pythonApi.post(`/api/v1/tasks/${workflowId}/signal`, { signal_name: signal, status }),
    },

    // Onboarding Flow (Python backend)
    onboardingFlow: {
        getStatus: (userId) => pythonApi.get(`/api/v1/onboarding/status?user_id=${userId}`),
        advance: (userId, step) => pythonApi.post(`/api/v1/onboarding/advance?user_id=${userId}`, { step }),
        skipStep: (userId, step) => pythonApi.post(`/api/v1/onboarding/skip-step?user_id=${userId}`, { step }),
        skip: (userId) => pythonApi.post(`/api/v1/onboarding/skip?user_id=${userId}`),
        answer: (userId, question, answer) => pythonApi.post(`/api/v1/onboarding/answer?user_id=${userId}`, { question, answer }),
        getProgress: (userId, taskType = 'emails') => pythonApi.get(`/api/v1/onboarding/progress?user_id=${userId}&task_type=${taskType}`),
        startIngestion: (userId, taskType = 'emails') => pythonApi.post(`/api/v1/onboarding/start-ingestion?user_id=${userId}`, taskType),
        complete: (userId) => pythonApi.post(`/api/v1/onboarding/complete?user_id=${userId}`),
        reset: (userId) => pythonApi.post(`/api/v1/onboarding/reset?user_id=${userId}`),
    },

    // Chat History
    chat: {
        getTodaySession: (userId) => pythonApi.post(`/api/v1/chat/sessions/today?user_id=${userId}`),
        getSessions: (userId) => pythonApi.get(`/api/v1/chat/sessions?user_id=${userId}`),
        createSession: (data) => pythonApi.post('/api/v1/chat/sessions', data),
        getMessages: (sessionId) => pythonApi.get(`/api/v1/chat/sessions/${sessionId}/messages`),
        updateSession: (sessionId, data) => pythonApi.put(`/api/v1/chat/sessions/${sessionId}`, data),
        deleteSession: (sessionId) => pythonApi.delete(`/api/v1/chat/sessions/${sessionId}`),
        sendMessage: (data) => pythonApi.post('/api/v1/chat/message', data),
    },
};

export default api;
